import { Placeholder } from "@tiptap/extensions";
import { EditorContent, useEditor } from "@tiptap/react";
import { StarterKit } from "@tiptap/starter-kit";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useAccessToken } from "@/auth/useAccessToken";
import { useChatStore } from "@/runtime/chatStore";
import { pumpJobStream } from "@/runtime/ssePumpJob";
import { AbortButton } from "./AbortButton";
import { handleComposerKeyDown, stripPastedHTML } from "./composerKeyHandler";

const RECONNECT_MAX_ATTEMPTS = 5;

function SendButton({ disabled }: { disabled: boolean }) {
  return (
    <button
      type="submit"
      aria-label="Send message"
      title="Send (Enter)"
      disabled={disabled}
      className={`flex h-9 w-9 items-center justify-center rounded-full text-[var(--text-on-accent)] transition-all duration-[var(--dur-fast)] ease-[var(--ease-out)] focus:outline-none focus-visible:shadow-[var(--elev-focus)] ${
        disabled
          ? "cursor-not-allowed bg-[var(--border-strong)] shadow-none"
          : "bg-[var(--accent-primary)] shadow-[var(--elev-whisper)] hover:bg-[var(--accent-hover)] hover:shadow-[var(--elev-whisper-lg)]"
      }`}
    >
      <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
        <path d="M8 2.5l5.5 5.5-1.06 1.06L8.75 5.81V13.5h-1.5V5.81L3.56 9.06 2.5 8 8 2.5z" />
      </svg>
    </button>
  );
}

export function Composer() {
  const isStreaming = useChatStore((s) => s.isStreaming);
  const answerReady = useChatStore((s) => s.answerReady);
  const activeTenantId = useChatStore((s) => s.activeTenantId);
  const activeConvId = useChatStore((s) => s.activeConversationId);
  const reconnectState = useChatStore((s) => s.reconnectState);
  const composerDraft = useChatStore((s) => s.composerDraft);
  const setComposerDraft = useChatStore((s) => s.setComposerDraft);
  const composerFocusSignal = useChatStore((s) => s.composerFocusSignal);
  const { acquireToken } = useAccessToken();
  const navigate = useNavigate();

  const placeholderText = activeTenantId
    ? "Ask a question…  (Enter to send · Shift+Enter for newline)"
    : "Select a tenant first";
  const placeholderRef = useRef(placeholderText);
  placeholderRef.current = placeholderText;

  const submitRef = useRef<((text: string) => void) | null>(null);
  const [hasContent, setHasContent] = useState(false);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false,
        bold: false,
        italic: false,
        strike: false,
        code: false,
        codeBlock: false,
        blockquote: false,
        bulletList: false,
        orderedList: false,
        listItem: false,
        horizontalRule: false,
      }),
      Placeholder.configure({
        placeholder: () => placeholderRef.current,
      }),
    ],
    immediatelyRender: false,
    editorProps: {
      attributes: {
        role: "textbox",
        "aria-multiline": "true",
        "aria-label": "Message composer",
      },
      handleKeyDown(view, event) {
        return handleComposerKeyDown(view, event, (text) => {
          submitRef.current?.(text);
        });
      },
      transformPastedHTML(html) {
        return stripPastedHTML(html);
      },
    },
    onUpdate({ editor: ed }) {
      setHasContent(ed.getText().trim().length > 0);
    },
  });

  async function submitMessage(text: string) {
    if (!text.trim() || (isStreaming && !answerReady) || !activeTenantId) return;
    const token = await acquireToken();
    // Iter 22 — auto-initialize activeConversationId so backend can group
    // jobs into a thread. Without this, activeConvId stays null forever on
    // fresh-state "/" URLs, POST sends `conversation_id: undefined` which
    // JSON.stringify drops, routes/query_jobs.py:233's `is not None` guard
    // skips storing it on request_jsonb, and runtime.py:537's multiturn
    // fetch short-circuits → iter 20 + iter 21 multiturn infrastructure
    // unreachable. Capture wasNewThread BEFORE ensure so we only navigate
    // URL on first-message-of-fresh-thread (no churn on subsequent).
    const wasNewThread = activeConvId === null;
    const convId = useChatStore.getState().ensureActiveConversation();
    const pumpArgs = {
      tenantId: activeTenantId,
      text,
      conversationId: convId,
      includeTrace: true,
    };
    // A resubmit DURING the previous answer's citation phase aborts the prior
    // pump; ssePumpJob's active-controller guard stops its late onClose/onEvent
    // from clobbering the new stream the next pumpJobStream is about to start.
    if (useChatStore.getState().isStreaming) {
      useChatStore.getState().abortController?.abort();
    }
    void pumpJobStream(pumpArgs, token);
    editor?.commands.clearContent();
    setHasContent(false);
    // URL bookmarkability: navigate to /c/<id> on first submit so a refresh
    // restores the thread via useUrlConversationSync. Safe wrt message
    // reset because ensureActiveConversation already set store.convId,
    // so useUrlConversationSync's `urlConvId !== activeConvId` check is
    // false → setActiveConversation NOT called → messages preserved.
    if (wasNewThread) {
      navigate(`/c/${convId}`);
    }
  }

  submitRef.current = submitMessage;

  useEffect(() => {
    if (!editor) return;
    if (composerDraft) {
      editor.commands.setContent(composerDraft, { emitUpdate: true });
      editor.commands.focus("end");
      setComposerDraft("");
    }
  }, [editor, composerDraft, setComposerDraft]);

  useEffect(() => {
    if (!editor) return;
    if (composerFocusSignal > 0) {
      editor.commands.focus("end");
      editor.commands.selectAll();
    }
  }, [editor, composerFocusSignal]);

  useEffect(() => {
    if (!editor) return;
    editor.setEditable((!isStreaming || answerReady) && !!activeTenantId);
  }, [editor, isStreaming, answerReady, activeTenantId]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!editor) return;
    await submitMessage(editor.getText());
  }

  return (
    <div className="flex flex-col gap-1">
      {reconnectState && (
        <div
          role="status"
          aria-live="polite"
          className="flex items-center gap-2 px-[var(--space-2)] py-[var(--space-1)] type-caption text-[var(--text-secondary)]"
        >
          <span
            aria-hidden="true"
            className="inline-block h-2 w-2 animate-pulse rounded-full bg-[var(--accent-primary)]"
          />
          <span>
            Reconnecting (attempt {reconnectState.attempt}/{RECONNECT_MAX_ATTEMPTS})…
          </span>
        </div>
      )}
      <form
        onSubmit={onSubmit}
        className="bk-composer flex items-end gap-[var(--space-2)] rounded-[var(--radius-xl)] bg-[var(--surface-raised)] p-[var(--space-3)] shadow-[var(--elev-ring)] transition-all duration-[var(--dur-fast)] ease-[var(--ease-out)] focus-within:shadow-[0_0_0_1px_var(--accent-primary),var(--elev-whisper-lg)]"
      >
        <EditorContent editor={editor} className="min-w-0 flex-1" />
        {isStreaming && !answerReady ? <AbortButton /> : <SendButton disabled={!hasContent || !activeTenantId} />}
      </form>
    </div>
  );
}
